import React, { useEffect } from "react";
import { X } from "lucide-react";

/**
 * Lightweight image lightbox. Uses a portal-free fixed overlay.
 * Closes on backdrop click, Escape key, or X button.
 */
export default function Lightbox({ open, onClose, src, alt, caption, subcaption }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={caption || "Image preview"}
      data-testid="lightbox"
      className="fixed inset-0 z-[100] bg-slate-950/95 flex flex-col items-center justify-center p-4 md:p-10"
    >
      <button
        onClick={onClose}
        aria-label="Close"
        data-testid="lightbox-close"
        className="absolute top-4 right-4 md:top-6 md:right-6 w-11 h-11 bg-white/10 hover:bg-white/20 text-white rounded-full flex items-center justify-center transition-colors"
      >
        <X size={22} />
      </button>
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative max-w-6xl max-h-[85vh] w-full flex flex-col items-center justify-center"
      >
        <img
          src={src}
          alt={alt}
          className="max-w-full max-h-[78vh] object-contain"
          loading="eager"
          decoding="async"
        />
        {(caption || subcaption) && (
          <div className="mt-5 text-center text-white">
            {caption && <div className="font-display text-xl md:text-2xl font-semibold">{caption}</div>}
            {subcaption && <div className="overline text-white/60 mt-2">{subcaption}</div>}
          </div>
        )}
      </div>
    </div>
  );
}
